import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Search,
  Brain,
  Heart,
  Globe,
  Layers,
  Users,
  DollarSign,
  Tag,
  Sparkles,
  Database,
  RefreshCw,
  Clock,
  CheckSquare,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Zap
} from 'lucide-react';

export default function CategoryResearchGuide() {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <Link
          to="/help/guides"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to User Guides
        </Link>
        <h1 className="text-3xl font-bold mb-2">Category Research Guide</h1>
        <p className="text-muted-foreground">
          Understanding and running category research for better AEO content generation
        </p>
        <div className="flex gap-2 mt-3">
          <Badge>Super Admin</Badge>
          <Badge>Content Manager</Badge>
        </div>
      </div>

      {/* What is Category Research */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-500" />
            What is Category Research?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            Category research is PageMind's AI-powered system that discovers what customers <strong>actually care about</strong> when
            choosing products in a specific category. It bridges the gap between technical specifications and customer needs.
          </p>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm font-medium mb-2">The Core Problem It Solves:</p>
            <p className="text-sm text-muted-foreground">
              Your product feed says "TPI: 120" but customers ask "Will this tire feel smooth on long rides?"
              Category research teaches PageMind that TPI=120 means "premium performance, lighter and suppler ride feel."
            </p>
          </div>

          <p className="text-sm">
            Category research generates <strong>three types of knowledge</strong>:
          </p>

          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <Heart className="h-5 w-5 text-pink-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Perceived Benefits</p>
                <p className="text-sm text-muted-foreground">
                  What customers care about when choosing products (e.g., "Battery Range & Freedom", "Comfort on Long Rides")
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <Database className="h-5 w-5 text-purple-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Attribute Intelligence</p>
                <p className="text-sm text-muted-foreground">
                  What technical spec values mean to customers (e.g., "TPI 120 = performance grade, lighter feel")
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <Layers className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Category Characteristics</p>
                <p className="text-sm text-muted-foreground">
                  Market structure: personas, price tiers, brand tiers, use cases, and vocabulary
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Where to Find It */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-green-500" />
            Where to Find It
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-3">
            <strong>Location:</strong> Research &rarr; Category Perception Research
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Or navigate directly to: <code className="bg-muted px-1 rounded">/research/category-perceptions</code>
          </p>
          <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm">
              <strong>Important:</strong> Category research is done at the <strong>sector level</strong>, not the site level.
              All sites assigned to a sector share the same research knowledge.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Perceived Benefits Deep Dive */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-pink-500" />
            Perceived Benefits (The 3-Layer Framework)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm">
            Perceived benefits are organized into <strong>three layers</strong> that represent different levels of customer motivation:
          </p>

          {/* Functional Layer */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-blue-50 dark:bg-blue-950/30 p-3 border-b">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-blue-500" />
                <span className="font-semibold">Functional Layer</span>
                <Badge variant="outline" className="ml-2">Practical & Measurable</Badge>
              </div>
            </div>
            <div className="p-3">
              <p className="text-sm text-muted-foreground mb-2">
                Benefits that solve practical problems and can be measured objectively.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Performance</Badge>
                <Badge variant="secondary">Convenience</Badge>
                <Badge variant="secondary">Protection</Badge>
                <Badge variant="secondary">Adaptability</Badge>
                <Badge variant="secondary">Innovation</Badge>
              </div>
              <p className="text-sm mt-2 italic text-muted-foreground">
                Example: "Battery Range & Freedom" - How far the e-bike can travel on a single charge
              </p>
            </div>
          </div>

          {/* Emotional Layer */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-pink-50 dark:bg-pink-950/30 p-3 border-b">
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-500" />
                <span className="font-semibold">Emotional Layer</span>
                <Badge variant="outline" className="ml-2">Feelings & Confidence</Badge>
              </div>
            </div>
            <div className="p-3">
              <p className="text-sm text-muted-foreground mb-2">
                Benefits that address how customers feel when using the product.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Freedom</Badge>
                <Badge variant="secondary">Confidence</Badge>
                <Badge variant="secondary">Joy</Badge>
                <Badge variant="secondary">Pride</Badge>
                <Badge variant="secondary">Achievement</Badge>
              </div>
              <p className="text-sm mt-2 italic text-muted-foreground">
                Example: "Riding Confidence" - Feeling secure and in control on any terrain
              </p>
            </div>
          </div>

          {/* Aspirational Layer */}
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-green-50 dark:bg-green-950/30 p-3 border-b">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-green-500" />
                <span className="font-semibold">Aspirational Layer</span>
                <Badge variant="outline" className="ml-2">Lifestyle & Identity</Badge>
              </div>
            </div>
            <div className="p-3">
              <p className="text-sm text-muted-foreground mb-2">
                Benefits that connect to who customers want to be or how they want to live.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">Health</Badge>
                <Badge variant="secondary">Sustainability</Badge>
                <Badge variant="secondary">Modernity</Badge>
                <Badge variant="secondary">Balance</Badge>
                <Badge variant="secondary">Belonging</Badge>
              </div>
              <p className="text-sm mt-2 italic text-muted-foreground">
                Example: "Eco-Conscious Commuting" - Contributing to a greener planet through transportation choices
              </p>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">What Each Perceived Benefit Includes:</h4>
            <ul className="text-sm space-y-2 ml-4 list-disc text-muted-foreground">
              <li><strong>Name:</strong> Customer-friendly benefit name</li>
              <li><strong>Description:</strong> What this benefit means to customers</li>
              <li><strong>Common Phrases:</strong> How customers talk about this ("long battery life", "smooth ride")</li>
              <li><strong>Customer Voice Samples:</strong> Actual questions/statements with sentiment (positive/negative/question)</li>
              <li><strong>Technical Features:</strong> Which specs affect this benefit (TPI, weight, motor efficiency)</li>
              <li><strong>Purchase Driver Strength:</strong> How important this is in buying decisions (1-10)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Attribute Intelligence */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-500" />
            Attribute Intelligence (Value Interpretations)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            Attribute intelligence explains what <strong>technical spec values mean to customers</strong>.
            This is critical for generating accurate, helpful AEO content.
          </p>

          <div className="bg-muted/50 rounded-lg p-4">
            <p className="font-medium text-sm mb-2">Example: TPI (Threads Per Inch) for Bicycle Tires</p>
            <div className="border rounded-lg overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 font-medium">Value</th>
                    <th className="text-left p-2 font-medium">Customer Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2 font-mono">60 TPI</td>
                    <td className="p-2 text-muted-foreground">Standard, durable. Good for training and rough terrain.</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">120 TPI</td>
                    <td className="p-2 text-muted-foreground">Performance grade. Lighter, more supple. Preferred for racing.</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono">180+ TPI</td>
                    <td className="p-2 text-muted-foreground">Race-grade premium. Maximum suppleness and speed.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              <strong>Customer Benefit:</strong> Higher TPI feels faster and absorbs road vibrations better.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-sm">
              <strong>Note:</strong> Attribute intelligence is stored at the <strong>sector level</strong> and shared
              across all sites in the sector. Research it once, benefit everywhere.
            </p>
          </div>

          <div>
            <h4 className="font-medium mb-2">How Attribute Intelligence is Used:</h4>
            <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
              <li>Generates accurate product descriptions ("This premium 120 TPI tire delivers a noticeably smoother ride")</li>
              <li>Answers spec-related questions ("What's the difference between 60 and 120 TPI?")</li>
              <li>Validates AI-generated content (prevents hallucinations about spec meanings)</li>
              <li>Powers comparison features ("This model has higher TPI for better performance")</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Category Characteristics */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-orange-500" />
            Category Characteristics (Market Structure)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            Category characteristics provide market context that helps PageMind understand <strong>how products are positioned</strong> and
            <strong>who buys them</strong>. The system researches 10 different aspects:
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            {/* Personas */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-sm">Personas</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Target buyer segments with descriptions, needs, and price sensitivity.
              </p>
              <p className="text-xs mt-1 italic">
                Example: "Weekend Warrior", "Commuter", "Performance Enthusiast"
              </p>
            </div>

            {/* Price Tiers */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="font-medium text-sm">Price Tiers</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Market-based price ranges with associated personas.
              </p>
              <p className="text-xs mt-1 italic">
                Example: Entry (&le;50), Mid-range (50-150), Premium (150-300), Ultra-premium (300+)
              </p>
            </div>

            {/* Brand Tiers */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="h-4 w-4 text-purple-500" />
                <span className="font-medium text-sm">Brand Tiers</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Classification of brands by market position.
              </p>
              <p className="text-xs mt-1 italic">
                Example: Premium (Continental, Schwalbe), Mid (Maxxis), Entry (Kenda)
              </p>
            </div>

            {/* Tier Vocabulary */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-pink-500" />
                <span className="font-medium text-sm">Tier Vocabulary</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Language to use (and avoid) for each price tier.
              </p>
              <p className="text-xs mt-1 italic">
                Premium: Use "precision", avoid "budget". Entry: Use "value", avoid "cheap"
              </p>
            </div>

            {/* Use Cases */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-yellow-500" />
                <span className="font-medium text-sm">Use Cases</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Patterns to detect usage from product names/descriptions.
              </p>
              <p className="text-xs mt-1 italic">
                Example: "gravel", "road", "MTB", "commuter"
              </p>
            </div>

            {/* Spec Semantics */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-cyan-500" />
                <span className="font-medium text-sm">Spec Semantics</span>
              </div>
              <p className="text-xs text-muted-foreground">
                What spec value ranges mean (complements attribute intelligence).
              </p>
              <p className="text-xs mt-1 italic">
                Example: TPI 60-80 = "standard", 100-120 = "performance", 140+ = "race"
              </p>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Additional characteristics include: Product Types, Premium Features, Measurement Units, and Validation Rules.
          </p>
        </CardContent>
      </Card>

      {/* How to Run Research */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-500" />
            How to Run Category Research
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 1: Navigate to Category Research</h4>
            <p className="text-sm text-muted-foreground">
              Go to <strong>Research &rarr; Category Perception Research</strong>
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 2: Select Your Sector</h4>
            <p className="text-sm text-muted-foreground">
              Use the global sector selector in the header. All research is sector-level and will be shared across sites.
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 3: Enter Category Name</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Type the category you want to research (e.g., "DC Brushed Motors", "Bicycle Tires", "Running Shoes").
            </p>
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <strong>Tip:</strong> Use the exact category names from your product feed for best results.
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 4: Run Research (Three Buttons)</h4>
            <div className="space-y-3">
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">Research Category</span>
                  <Badge variant="outline">Required First</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Discovers perceived benefits using the 3-layer framework. This must be done first as other research depends on it.
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">Research Attribute Values</span>
                  <Badge variant="outline">Requires Perceptions</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Generates attribute intelligence - explains what spec values mean. Uses technical features discovered in step 1.
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">Refresh All Characteristics</span>
                  <Badge variant="outline">Optional</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Researches all 10 characteristics (personas, price tiers, brand tiers, etc.) in parallel.
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 5: Review Results</h4>
            <p className="text-sm text-muted-foreground mb-2">
              After research completes, review the four tabs:
            </p>
            <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
              <li><strong>Perceived Benefits:</strong> Customer-facing attributes with voice samples</li>
              <li><strong>Attribute Intelligence:</strong> Technical spec value interpretations</li>
              <li><strong>Category Characteristics:</strong> Market structure (personas, tiers, vocabulary)</li>
              <li><strong>Research Sessions:</strong> History of research runs with prompts used</li>
            </ul>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Step 6: Edit If Needed</h4>
            <p className="text-sm text-muted-foreground">
              Each perceived benefit can be edited or deleted. Click the edit icon to modify names, descriptions,
              or technical features. Delete any that don't make sense for your category.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Research Freshness */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Research Freshness & Costs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">Freshness Status</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Category characteristics have a 90-day TTL (time-to-live):
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Fresh</Badge>
              <span className="text-sm text-muted-foreground">Recently researched</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Aging</Badge>
              <span className="text-sm text-muted-foreground">Getting old, consider refreshing</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
              <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Stale</Badge>
              <span className="text-sm text-muted-foreground">Over 90 days old, refresh recommended</span>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">Research Costs</h4>
            <p className="text-sm text-muted-foreground">
              Research uses GPT-4o and costs are tracked. After each research operation, you'll see the cost displayed
              (typically $0.01-0.10 per category depending on depth). Costs accumulate and are shown in the UI.
            </p>
          </div>

          <Separator />

          <div>
            <h4 className="font-medium mb-2">When to Re-research</h4>
            <ul className="text-sm space-y-1 ml-4 list-disc text-muted-foreground">
              <li>When you add significantly new products to a category</li>
              <li>When market conditions change (new competitors, pricing shifts)</li>
              <li>When research is marked as "stale" (90+ days old)</li>
              <li>When AEO content quality seems off for a category</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Best Practices */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            Best Practices
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Research Before Feed Import</p>
                <p className="text-sm text-muted-foreground">
                  Run category research for your major categories before importing products. This ensures AEO content
                  is generated with full context from the start.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Use Exact Category Names</p>
                <p className="text-sm text-muted-foreground">
                  Research using the same category names in your feed (e.g., "DC Brushed Motors" not just "Motors").
                  This ensures research maps correctly to products.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Review Voice Samples</p>
                <p className="text-sm text-muted-foreground">
                  The customer voice samples show how real customers talk about benefits. Use these to validate
                  that research matches your market. Edit if your customers speak differently.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Research Parent and Child Categories</p>
                <p className="text-sm text-muted-foreground">
                  For hierarchical categories, research both the parent ("Motors") and specific children
                  ("DC Brushed Motors", "AC Servo Motors"). General research applies broadly, specific research adds detail.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium text-sm">Check Attribute Intelligence Coverage</p>
                <p className="text-sm text-muted-foreground">
                  After running attribute intelligence, verify that your key specifications are covered.
                  If important specs are missing, you may need to add them manually via Domain Knowledge.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Troubleshooting
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-medium text-sm">"Research Attribute Values" button is disabled</h4>
            <p className="text-sm text-muted-foreground">
              You must run "Research Category" first. Attribute intelligence needs the technical features discovered
              in perceived benefits research to know what to research.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">Research returns few or irrelevant benefits</h4>
            <p className="text-sm text-muted-foreground">
              Try using a more specific category name. "Tires" is too broad; "Road Bicycle Tires" gives better results.
              Also ensure your sector has product data the AI can learn from.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">AEO content still doesn't match customer language</h4>
            <p className="text-sm text-muted-foreground">
              Edit the perceived benefits and customer voice samples to match how YOUR customers actually speak.
              The research provides a starting point, but industry-specific language may need tuning.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">Personas don't match my market</h4>
            <p className="text-sm text-muted-foreground">
              The personas are AI-generated based on general market knowledge. For B2B or niche markets,
              you may need to adjust personas in the characteristics or add custom ones via Domain Knowledge.
            </p>
          </div>
          <Separator />
          <div>
            <h4 className="font-medium text-sm">Research is expensive / taking too long</h4>
            <p className="text-sm text-muted-foreground">
              Research uses GPT-4o which is slower and more expensive than smaller models. Only research categories
              you actually need. Characteristics research runs 10 parallel operations - this is normal.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Related Links */}
      <Card className="mb-6 bg-muted/50">
        <CardContent className="pt-6">
          <h4 className="font-medium mb-3">Related Resources</h4>
          <div className="space-y-2">
            <Link
              to="/help/guides/onboarding"
              className="flex items-center text-sm text-primary hover:underline"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              Website Onboarding Guide (uses category research in Phase 1 & 4)
            </Link>
            <Link
              to="/research/category-perceptions"
              className="flex items-center text-sm text-primary hover:underline"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              Go to Category Research Tool
            </Link>
            <Link
              to="/domain-knowledge"
              className="flex items-center text-sm text-primary hover:underline"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              Domain Knowledge (for manual attribute knowledge)
            </Link>
            <Link
              to="/help/faq"
              className="flex items-center text-sm text-primary hover:underline"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              FAQ
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
